import {
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import React, { useEffect, useMemo, useState } from 'react'

import {
  getPreschoolTeacherTrends,
  getPreschoolUpdatesForDate
} from 'api/preschoolUpdatesAudit'
import {
  computeChildMatrix,
  computeTeacherTrend,
  TEACHER_TREND_METRICS,
  UPDATE_COLUMNS
} from 'app/pages/Audits/PreschoolDailyUpdates/data'
import {
  DatePicker,
  DrillDownDialog,
  formatDate,
  latestWeekday,
  PRESENCE_FILTERS,
  StatStrip,
  TrendChart,
  TrendSummaryLine,
  UpdateCell,
  useDrillDown
} from 'app/pages/Audits/PreschoolDailyUpdates/PreschoolDailyUpdates'
import useStyles from 'app/pages/Audits/PreschoolDailyUpdates/styles'

// Everything on this page is live. The Day tab's children are the teacher's
// real class: every row and absentee from the audit endpoint carries
// `teachers`, the teachers mapped to that child's batch on the day
// (oh_moments/services/preschool_update_audit.py). Co-teachers of a batch are
// equal, so each sees the whole class and a missed update counts on all of
// their pages.
//
// This used to come from a static teacherTrends.json frozen at 18 Jun-17 Jul
// (OH-1751): a teacher or child who arrived after that had no roster, so Ifath
// read "0 of 0" however many updates she sent.
//
// The Trend tab is different: it's this teacher's own posting history
// specifically (late %, non-personalised %, as a rate of what they actually
// posted) - there's no "required" ground truth to trend against per
// teacher, only per centre/roster.

const teachesChild = (teacher) => (r) => (r.teachers || []).includes(teacher)

// Every teacher on a batch with a session that day, for the picker.
const teachersOnDay = (dayData) => {
  const names = new Set()
  ;[...dayData.rows, ...dayData.absentees].forEach((r) =>
    (r.teachers || []).forEach((t) => names.add(t))
  )
  return [...names].sort((a, b) => a.localeCompare(b))
}

const DayTab = ({ teacher, dateLabel, dayData, onDrillDown }) => {
  const classes = useStyles()
  const [presenceFilter, setPresenceFilter] = useState('ALL')

  if (!dayData) {
    return (
      <div className={classes.loadingWrap}>
        <CircularProgress size={24} />
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginLeft: 12 }}
        >
          Loading {dateLabel}...
        </Typography>
      </div>
    )
  }

  // The class says WHICH CHILDREN belong on this page. It does not say
  // whose work the update was, and leaving it there was the bug (OH-1751):
  // every centre has two or more teachers posting for the same children, so a
  // colleague's updates were credited to whoever the roster is filed under.
  // Ifath's updates all showed under Samiksha for exactly this reason.
  //
  // `postedBy` is the uploader and the backend has always sent it
  // (oh_moments/services/preschool_update_audit.py). The Audits page already
  // filters on it; this page never did.
  //
  // MISSING rows are kept deliberately. They are built when NO update exists
  // (postedBy is null by construction), so matching them against a name would
  // drop every one of them and empty the "never sent" tile — which is the other
  // half of what was reported. A missing update belongs to the roster-holder,
  // because it is the child who was missed, not a message someone sent.
  //
  // Colleagues' rows still go into the grid (OH-HRBR-261005-4535): dropping
  // them made the cell read "n/a" ("not required") for an update that was
  // sent. They render as a neutral "Sent by <name>" chip and stay out of the
  // counts, which use ownRows only. Own rows go last so they win a same-slot tie.
  const childRows = dayData.rows.filter(teachesChild(teacher))
  const isOwn = (r) => r.status === 'MISSING' || r.postedBy === teacher
  const ownRows = childRows.filter(isOwn)
  const colleagueRows = childRows.filter((r) => !isOwn(r))
  const rosterAbsentees = dayData.absentees.filter(teachesChild(teacher))
  const centres = [
    ...new Set([...childRows, ...rosterAbsentees].map((r) => r.centre))
  ].join(', ')
  const children = computeChildMatrix(
    [...colleagueRows, ...ownRows],
    rosterAbsentees
  )
  const filteredChildren = children.filter((c) => {
    if (presenceFilter === 'PRESENT') return c.present
    if (presenceFilter === 'ABSENT') return !c.present
    return true
  })
  const presentCount = children.filter((c) => c.present).length

  return (
    <div>
      <StatStrip rows={ownRows} onDrillDown={onDrillDown} />

      <div className={classes.filterRow}>
        {PRESENCE_FILTERS.map((f) => (
          <Chip
            key={f.key}
            label={f.label}
            size="small"
            clickable
            color={presenceFilter === f.key ? 'primary' : 'default'}
            onClick={() => setPresenceFilter(f.key)}
            className={classes.filterChip}
          />
        ))}
      </div>

      <TableContainer
        component={Paper}
        variant="outlined"
        className={classes.tableWrap}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Child</TableCell>
              <TableCell>Present</TableCell>
              {UPDATE_COLUMNS.map((col) => (
                <TableCell key={col.key}>{col.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredChildren.map((c) => (
              <TableRow key={c.child}>
                <TableCell>{c.child}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={c.present ? 'Present' : 'Absent'}
                    className={
                      c.present ? classes.chipOnTime : classes.chipAbsent
                    }
                  />
                </TableCell>
                {UPDATE_COLUMNS.map((col) => (
                  <TableCell key={col.key}>
                    <UpdateCell
                      child={c}
                      column={col}
                      onDrillDown={onDrillDown}
                      creditTo={teacher}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography className={classes.sampleNote}>
          {filteredChildren.length} of {children.length} children in this
          teacher&apos;s classes
          {centres ? ` at ${centres}` : ''} on {dateLabel} ({presentCount}{' '}
          present, {children.length - presentCount} absent). Classes are the
          batches she is mapped to; co-teachers share the class, so a missed
          update shows on each of their pages.
        </Typography>
      </TableContainer>
    </div>
  )
}

const TrendTab = ({ trendData, trendError, teacher }) => {
  const classes = useStyles()

  if (trendError) {
    return (
      <Paper variant="outlined" className={classes.anomalyNote}>
        <Typography variant="body2">{trendError}</Typography>
      </Paper>
    )
  }

  if (!trendData) {
    return (
      <div className={classes.loadingWrap}>
        <CircularProgress size={24} />
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginLeft: 12 }}
        >
          Computing the last 30 days of trends...
        </Typography>
      </div>
    )
  }

  return (
    <div>
      {TEACHER_TREND_METRICS.map((metric) => {
        const series = computeTeacherTrend(trendData, teacher, metric.key)
        return (
          <div key={metric.key} className={classes.trendBlock}>
            <Typography variant="subtitle2" className={classes.trendBlockTitle}>
              {metric.label}
            </Typography>
            <Paper variant="outlined" className={classes.trendCard}>
              {series.length > 0 ? (
                <TrendChart
                  series={series}
                  metricLabel={metric.label}
                  higherIsBetter={metric.higherIsBetter}
                />
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No posted updates for {teacher} in this window yet.
                </Typography>
              )}
              <TrendSummaryLine
                series={series}
                metricLabel={metric.label}
                higherIsBetter={metric.higherIsBetter}
              />
            </Paper>
          </div>
        )
      })}
      <Typography className={classes.footerNote}>
        These rates are of what this teacher personally posted (their account),
        computed live over the last ~30 weekdays. Each chart splits at the
        midpoint of the available dates - not a regression, just two period
        averages.
      </Typography>
    </div>
  )
}

const UpdateCompliance = () => {
  const classes = useStyles()
  const [tab, setTab] = useState('day')
  const {
    drillDown,
    open: openDrillDown,
    close: closeDrillDown
  } = useDrillDown()

  const [selectedDate, setSelectedDate] = useState(latestWeekday)
  const [selectedTeacher, setSelectedTeacher] = useState(null)
  const [dayData, setDayData] = useState(null) // { rows, absentees } | null
  const [trendData, setTrendData] = useState(null) // { dates, byDate } | null - live
  const [trendError, setTrendError] = useState(null)
  const [error, setError] = useState(null)

  // Trends are computed live from the backend over a rolling ~30-day window
  // (all teachers at once, in one call). Fetched lazily the first time the
  // Trend tab is opened - it's a heavier query than a single day, so there's
  // no reason to pay for it on a page that only ever views the Day tab.
  useEffect(() => {
    if (tab !== 'trend' || trendData || trendError) return
    getPreschoolTeacherTrends()
      .then(setTrendData)
      .catch(() => setTrendError('Could not load live teacher trends.'))
  }, [tab, trendData, trendError])

  // Per-date rows are computed live from the backend (same endpoint the Audits
  // page uses), so any past date works - not just the handful of static files.
  // The teacher list comes from the same day's data.
  useEffect(() => {
    if (!selectedDate) return
    setDayData(null)
    setError(null)
    getPreschoolUpdatesForDate(selectedDate)
      .then((data) => {
        setDayData(data)
        setSelectedTeacher((cur) => cur || teachersOnDay(data)[0] || null)
      })
      .catch(() => setError(`Could not load audit data for ${selectedDate}.`))
  }, [selectedDate])

  // Keep the chosen teacher selectable on a day she had no session, so
  // switching dates never silently swaps whose page this is.
  const teacherOptions = useMemo(() => {
    if (!dayData) return selectedTeacher ? [selectedTeacher] : []
    const names = teachersOnDay(dayData)
    return selectedTeacher && !names.includes(selectedTeacher)
      ? [...names, selectedTeacher]
      : names
  }, [dayData, selectedTeacher])

  // A day with no preschool sessions has no teachers to pick from; show the
  // (empty) page for it rather than spinning forever.
  const loading = !selectedTeacher && !dayData

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h5" style={{ fontWeight: 700 }}>
          Daily Updates Report
        </Typography>
      </div>

      {error && (
        <Paper variant="outlined" className={classes.anomalyNote}>
          <Typography variant="body2">{error}</Typography>
        </Paper>
      )}

      {loading && !error ? (
        <div className={classes.loadingWrap}>
          <CircularProgress size={28} />
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginLeft: 12 }}
          >
            Loading teachers...
          </Typography>
        </div>
      ) : (
        !error && (
          <>
            <div className={classes.teacherPickerRow}>
              <Autocomplete
                options={teacherOptions}
                value={selectedTeacher}
                onChange={(e, v) => v && setSelectedTeacher(v)}
                disableClearable
                className={classes.teacherAutocomplete}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Teacher"
                    variant="outlined"
                    size="small"
                  />
                )}
              />
              {tab === 'day' && (
                <DatePicker
                  selectedDate={selectedDate}
                  onChange={setSelectedDate}
                />
              )}
            </div>

            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              indicatorColor="primary"
              textColor="primary"
              style={{ marginBottom: 24, borderBottom: '2px solid #eee' }}
            >
              <Tab value="day" label="Day" style={{ textTransform: 'none' }} />
              <Tab
                value="trend"
                label="Trend"
                style={{ textTransform: 'none' }}
              />
            </Tabs>

            {tab === 'day' && (
              <DayTab
                teacher={selectedTeacher}
                dateLabel={formatDate(selectedDate)}
                dayData={dayData}
                onDrillDown={openDrillDown}
              />
            )}
            {tab === 'trend' && (
              <TrendTab
                trendData={trendData}
                trendError={trendError}
                teacher={selectedTeacher}
              />
            )}
          </>
        )
      )}

      {drillDown && (
        <DrillDownDialog
          open={Boolean(drillDown)}
          onClose={closeDrillDown}
          title={drillDown.title}
          rows={drillDown.rows}
        />
      )}
    </div>
  )
}

export default UpdateCompliance
